import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Buildings, PencilSimple, Plus, Warning } from 'phosphor-react';
import { toast } from 'sonner';
import {
  centralApi,
  IEmpresaCarteira,
  IGrupoResumo,
  identificacaoDaEmpresa,
  podeBuscarEmpresa,
} from '../../hooks/useCentralPermissoes';
import {
  AZUL,
  AZUL_ESCURO,
  BarraSalvar,
  Busca,
  Cartao,
  Contador,
  Etiqueta,
  Eyebrow,
  MONO,
  TEXTO,
  TEXTO_SUAVE,
} from './ui';
import { detalheDoMotivo, empresaAtiva, MOTIVOS, situacaoDaEmpresa } from './situacaoEmpresa';
import { ResumoDaSituacao } from './EmpresasSituacao';

const GRUPO_ADMIN = 1;
const GRUPO_CLIENTE = 7;
const VISIVEIS = 10;

const erroDaApi = (e: any, padrao: string) => e?.response?.data?.detail ?? padrao;

/** Nomes e contagens dos grupos de empresas (cai para a lista simples se faltar a rota). */
export function useGruposDeEmpresas() {
  const resumo = useQuery({
    queryKey: ['central', 'grupos-empresas-resumo'],
    queryFn: centralApi.resumoGruposDeEmpresas,
    retry: false,
  });
  const nomes = useQuery({
    queryKey: ['central', 'carteiras-disponiveis'],
    queryFn: centralApi.carteirasDisponiveis,
    enabled: resumo.isError,
  });
  const lista = resumo.data ?? (nomes.data ?? []).map(nome => ({ nome, total_empresas: -1 }));
  return {
    lista,
    carregando: resumo.isLoading || (resumo.isError && nomes.isLoading),
    semRota: resumo.isError,
  };
}

/** Lista da esquerda no modo Empresas, com "Novo grupo de empresas". */
export function ListaGruposDeEmpresas({
  selecionado,
  rascunhos,
  onSelecionar,
  onNovo,
  onAlocar,
  inativasAbertas,
  onVerInativas,
}: {
  selecionado: string | null;
  rascunhos: string[];
  onSelecionar: (nome: string) => void;
  onNovo: (nome: string) => void;
  onAlocar: () => void;
  inativasAbertas: boolean;
  onVerInativas: () => void;
}) {
  const { lista, carregando } = useGruposDeEmpresas();
  const [busca, setBusca] = useState('');
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const limpo = nome.trim();
  const todos = [
    ...rascunhos
      .filter(r => !lista.some(g => g.nome === r))
      .map(r => ({ nome: r, total_empresas: 0, rascunho: true })),
    ...lista.map(g => ({ ...g, rascunho: false })),
  ];
  const repetido = todos.some(g => g.nome.toLowerCase() === limpo.toLowerCase());
  const q = busca.trim().toLowerCase();

  const alocando = !selecionado && !inativasAbertas;
  return (
    <>
      <ResumoDaSituacao aberto={inativasAbertas} onVerInativas={onVerInativas} />
      <Button
        size="sm"
        variant={alocando ? 'solid' : 'outline'}
        colorScheme={alocando ? 'blue' : undefined}
        borderRadius="999px"
        leftIcon={<Buildings size={14} />}
        onClick={onAlocar}
      >
        Alocar empresa
      </Button>
      <Busca valor={busca} onChange={setBusca} placeholder="Buscar grupo de empresas" />
      <Flex align="center" justify="space-between" gap="8px" wrap="wrap">
        <Eyebrow>GRUPOS DE EMPRESAS</Eyebrow>
        {!criando && (
          <Button
            size="sm"
            variant="outline"
            borderRadius="999px"
            bg="rgba(255,255,255,.7)"
            leftIcon={<Plus size={14} />}
            onClick={() => setCriando(true)}
          >
            Novo grupo
          </Button>
        )}
      </Flex>
      {criando && (
        <Flex
          as="form"
          gap="8px"
          onSubmit={e => {
            e.preventDefault();
            if (!limpo || repetido) return;
            onNovo(limpo);
            setNome('');
            setCriando(false);
          }}
        >
          <Input
            autoFocus
            size="sm"
            borderRadius="10px"
            bg="white"
            placeholder="Nome do grupo de empresas"
            aria-label="Nome do grupo de empresas novo"
            value={nome}
            isInvalid={repetido}
            onChange={e => setNome(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') setCriando(false);
            }}
          />
          <Button
            type="submit"
            size="sm"
            borderRadius="999px"
            color="white"
            bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
            _hover={{ bg: AZUL_ESCURO }}
            isDisabled={!limpo || repetido}
          >
            Criar
          </Button>
        </Flex>
      )}
      <Flex direction="column" gap="4px" maxH="62vh" overflowY="auto">
        {carregando && <Spinner size="sm" color={AZUL} />}
        {todos
          .filter(g => !q || g.nome.toLowerCase().includes(q))
          .map(g => {
            const sel = g.nome === selecionado;
            return (
              <Flex
                key={g.nome}
                as="button"
                type="button"
                aria-pressed={sel}
                onClick={() => onSelecionar(g.nome)}
                align="center"
                justify="space-between"
                gap="10px"
                w="100%"
                p="10px 12px"
                borderRadius="14px"
                textAlign="left"
                bg={sel ? '#DFF3FF' : 'transparent'}
                boxShadow={sel ? 'inset 0 0 0 1px rgba(0,119,182,.25)' : undefined}
                _hover={sel ? undefined : { bg: 'rgba(255,255,255,.7)' }}
              >
                <Box minW={0}>
                  <Text fontSize="14px" fontWeight={600} color={TEXTO} noOfLines={1}>
                    {g.nome}
                  </Text>
                  <Text fontSize="12px" color={TEXTO_SUAVE}>
                    {g.rascunho && 'novo, ainda sem empresas'}
                    {!g.rascunho && g.total_empresas >= 0 && `${g.total_empresas} empresas`}
                  </Text>
                </Box>
                {g.rascunho ? (
                  <Etiqueta tom="aviso">novo</Etiqueta>
                ) : (
                  g.total_empresas >= 0 && <Contador>{g.total_empresas}</Contador>
                )}
              </Flex>
            );
          })}
      </Flex>
    </>
  );
}

/** Painel do grupo de empresas: empresas dele, quem enxerga e renomear. */
export function GrupoDeEmpresasPainel({
  nome,
  novo,
  grupos,
  onRenomeado,
  onSalvo,
}: {
  nome: string;
  novo: boolean;
  grupos: IGrupoResumo[];
  onRenomeado: (novoNome: string) => void;
  onSalvo: () => void;
}) {
  const queryClient = useQueryClient();
  const { semRota } = useGruposDeEmpresas();
  const [empresas, setEmpresas] = useState<IEmpresaCarteira[]>([]);
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  const [renomeando, setRenomeando] = useState(false);
  const [novoNome, setNovoNome] = useState(nome);
  useEffect(() => {
    const t = setTimeout(() => setBusca(digitado.trim()), 300);
    return () => clearTimeout(t);
  }, [digitado]);

  const { data: membros, isLoading } = useQuery({
    queryKey: ['central', 'grupo-de-empresas', nome],
    queryFn: () => centralApi.empresasDoGrupoDeEmpresas(nome),
    enabled: !novo,
  });
  // inativas ficam fora da edição, mas continuam no grupo: entram de volta no salvar
  const inativasDoGrupo = useMemo(
    () => (novo ? [] : (membros ?? []).filter(e => !empresaAtiva(e))),
    [membros, novo],
  );
  const base = useMemo(() => (novo ? [] : (membros ?? []).filter(empresaAtiva)), [membros, novo]);
  useEffect(() => setEmpresas(base), [base]);
  const [verInativas, setVerInativas] = useState(false);

  const { data: achadas = [], isFetching } = useQuery({
    queryKey: ['central', 'busca-empresas', busca],
    queryFn: () => centralApi.buscarEmpresas(busca),
    enabled: podeBuscarEmpresa(busca),
  });

  // quem enxerga este grupo de empresas: grupos de colaboradores com ele na carteira
  const colaboradores = grupos.filter(g => g.id !== GRUPO_CLIENTE && g.id !== GRUPO_ADMIN);
  const carteiras = useQueries({
    queries: colaboradores.map(g => ({
      queryKey: ['central', 'grupo-carteira', g.id],
      queryFn: () => centralApi.carteiraDoGrupo(g.id),
    })),
  });
  const quemEnxerga = colaboradores.filter((g, i) => (carteiras[i]?.data ?? []).includes(nome));
  const verTodas = grupos.filter(g => g.ve_todas_carteiras && g.id !== GRUPO_ADMIN);

  const ids = new Set(empresas.map(e => e.id));
  const idsBase = new Set(base.map(e => e.id));
  const mudou = empresas.length !== base.length || empresas.some(e => !idsBase.has(e.id));
  const vindasDeOutro = empresas.filter(
    e => !idsBase.has(e.id) && e.grupo_de_empresas && e.grupo_de_empresas !== nome,
  );
  const entram = empresas.filter(e => !idsBase.has(e.id)).length;
  const saem = base.filter(e => !ids.has(e.id)).length;
  // campo vazio: as do grupo (e as que estão saindo); com texto: todas as empresas achadas
  const buscando = podeBuscarEmpresa(busca);
  const porId = new Map<number, IEmpresaCarteira>();
  if (buscando) achadas.forEach(e => porId.set(e.id, e));
  else [...base, ...empresas].forEach(e => porId.set(e.id, e));
  const linhas = Array.from(porId.values()).sort(
    (a, b) => Number(idsBase.has(b.id)) - Number(idsBase.has(a.id)) || a.nome.localeCompare(b.nome),
  );

  const salvar = useMutation({
    mutationFn: () =>
      centralApi.salvarGrupoDeEmpresas(nome, [
        ...empresas.map(e => e.id),
        ...inativasDoGrupo.map(e => e.id),
      ]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      toast.success(`Grupo de empresas ${nome} salvo`);
      onSalvo();
    },
    onError: e => toast.error(erroDaApi(e, 'Não foi possível salvar o grupo de empresas')),
  });

  const renomear = useMutation({
    mutationFn: () => centralApi.renomearGrupoDeEmpresas(nome, novoNome.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      toast.success(`Grupo de empresas renomeado para ${novoNome.trim()}`, {
        description:
          'As carteiras de grupos e usuários que usavam o nome antigo foram atualizadas.',
      });
      setRenomeando(false);
      onRenomeado(novoNome.trim());
    },
    onError: e => toast.error(erroDaApi(e, 'Não foi possível renomear o grupo de empresas')),
  });

  const alternar = (e: IEmpresaCarteira) =>
    setEmpresas(prev =>
      prev.some(x => x.id === e.id) ? prev.filter(x => x.id !== e.id) : [...prev, e],
    );

  const linha = (e: IEmpresaCarteira) => {
    const marcada = ids.has(e.id);
    const eraDoGrupo = idsBase.has(e.id);
    let etiqueta: React.ReactNode = null;
    if (marcada && !eraDoGrupo)
      etiqueta = (
        <Etiqueta tom="sucesso">
          {e.grupo_de_empresas && e.grupo_de_empresas !== nome
            ? `entra (sai de ${e.grupo_de_empresas})`
            : 'entra'}
        </Etiqueta>
      );
    else if (!marcada && eraDoGrupo) etiqueta = <Etiqueta tom="perigo">sai do grupo</Etiqueta>;
    else if (!marcada && e.grupo_de_empresas)
      etiqueta = <Etiqueta tom="neutro">hoje em {e.grupo_de_empresas}</Etiqueta>;
    else if (!marcada) etiqueta = <Etiqueta tom="neutro">sem grupo</Etiqueta>;
    return (
      <Flex
        key={e.id}
        as="label"
        align="center"
        gap="12px"
        p="9px 20px"
        cursor="pointer"
        borderTop="1px solid rgba(15,23,42,.05)"
        bg={marcada ? 'rgba(223,243,255,.75)' : undefined}
        boxShadow={marcada ? 'inset 3px 0 0 #0077B6' : undefined}
        _hover={{ bg: marcada ? 'rgba(223,243,255,.95)' : 'rgba(255,255,255,.7)' }}
      >
        <input
          type="checkbox"
          checked={marcada}
          onChange={() => alternar(e)}
          aria-label={`${marcada ? 'Tirar' : 'Pôr'} ${e.nome} no grupo`}
          style={{ width: 18, height: 18, accentColor: AZUL }}
        />
        <Buildings size={16} color={marcada ? AZUL : TEXTO_SUAVE} />
        <Box flex="1 1 240px" minW={0}>
          <Text
            fontSize="13.5px"
            fontWeight={600}
            noOfLines={1}
            color={marcada ? '#075985' : TEXTO}
          >
            {e.nome}
          </Text>
          <Text fontFamily={MONO} fontSize="11px" color={TEXTO_SUAVE}>
            {identificacaoDaEmpresa(e)}
          </Text>
        </Box>
        {etiqueta}
      </Flex>
    );
  };

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="14px">
        <Flex align="center" gap="14px" wrap="wrap">
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            bg="#E0E7FF"
            color="#3730A3"
          >
            <Buildings size={22} />
          </Flex>
          <Box flex="1 1 260px" minW={0}>
            <Eyebrow>GRUPO DE EMPRESAS</Eyebrow>
            {renomeando ? (
              <Flex
                as="form"
                gap="8px"
                mt="4px"
                onSubmit={e => {
                  e.preventDefault();
                  if (novoNome.trim() && novoNome.trim() !== nome) renomear.mutate();
                }}
              >
                <Input
                  autoFocus
                  size="sm"
                  borderRadius="10px"
                  bg="white"
                  value={novoNome}
                  aria-label="Novo nome do grupo de empresas"
                  onChange={e => setNovoNome(e.target.value)}
                />
                <Button
                  type="submit"
                  size="sm"
                  borderRadius="999px"
                  isLoading={renomear.isPending}
                  isDisabled={!novoNome.trim() || novoNome.trim() === nome}
                >
                  Renomear
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRenomeando(false)}>
                  Cancelar
                </Button>
              </Flex>
            ) : (
              <Flex align="center" gap="8px">
                <Text fontSize="22px" lineHeight="28px" fontWeight={700} letterSpacing="-.3px">
                  {nome}
                </Text>
                {!novo && (
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<PencilSimple size={12} />}
                    onClick={() => {
                      setNovoNome(nome);
                      setRenomeando(true);
                    }}
                  >
                    Renomear
                  </Button>
                )}
              </Flex>
            )}
            <Text fontSize="13px" color="#475569">
              {novo ? 'Grupo novo: adicione as empresas e salve.' : `${base.length} empresas`}
            </Text>
          </Box>
        </Flex>

        <Box>
          <Eyebrow>QUEM ENXERGA ESTE GRUPO DE EMPRESAS</Eyebrow>
          <Flex wrap="wrap" gap="6px" mt="6px">
            <Etiqueta tom="info">Administrador (vê tudo)</Etiqueta>
            {verTodas.map(g => (
              <Etiqueta key={`t-${g.id}`} tom="info">
                {g.nome} (vê todas)
              </Etiqueta>
            ))}
            {quemEnxerga
              .filter(g => !verTodas.some(v => v.id === g.id))
              .map(g => (
                <Etiqueta key={g.id} tom="sucesso">
                  {g.nome}
                </Etiqueta>
              ))}
            {quemEnxerga.length === 0 && verTodas.length === 0 && (
              <Text fontSize="12.5px" color={TEXTO_SUAVE}>
                Nenhum grupo de colaboradores tem este grupo de empresas na carteira.
              </Text>
            )}
          </Flex>
          <Text fontSize="12px" color={TEXTO_SUAVE} mt="6px">
            Para liberar, abra o grupo de usuários em Grupos → Carteira de empresas.
          </Text>
        </Box>

        {semRota && (
          <Flex
            gap="8px"
            align="flex-start"
            fontSize="12.5px"
            color="#92400E"
            bg="#FEFCE8"
            p="10px 12px"
            borderRadius="14px"
            boxShadow="inset 0 0 0 1px #FDE68A"
          >
            <Warning size={16} />
            <Text>
              Pré-visualização: as empresas de cada grupo já aparecem, mas criar, salvar e renomear
              dependem do backend de grupos de empresas, que ainda não está nesta versão.
            </Text>
          </Flex>
        )}
      </Cartao>

      <Cartao p={0} overflow="hidden">
        <Flex align="center" gap="10px" wrap="wrap" p="16px 20px 10px">
          <Box flex="1 1 240px">
            <Text fontSize="16px" fontWeight={650}>
              Empresas do grupo
            </Text>
            <Text fontSize="12.5px" color={TEXTO_SUAVE}>
              Marcadas em azul estão no grupo. Busque para achar qualquer empresa e clique para
              marcar ou desmarcar. Cada empresa fica em um só grupo de empresas.
            </Text>
          </Box>
          <Contador>{empresas.length}</Contador>
        </Flex>
        <Box px="20px" pb="12px" maxW="460px">
          <Busca
            valor={digitado}
            onChange={setDigitado}
            placeholder="Buscar empresa por nome, CNPJ ou ID"
          />
        </Box>
        {(isLoading || (buscando && isFetching)) && (
          <Flex justify="center" py={6}>
            <Spinner color={AZUL} />
          </Flex>
        )}
        {!isLoading && (
          <>
            {linhas.slice(0, VISIVEIS).map(linha)}
            {linhas.length > VISIVEIS && (
              <Text px="20px" py="8px" fontSize="12.5px" color={TEXTO_SUAVE}>
                Mostrando {VISIVEIS} de {linhas.length}. Use a busca para achar as outras.
              </Text>
            )}
            {linhas.length === 0 && !(buscando && isFetching) && (
              <Text px="20px" py="12px" fontSize="13px" color={TEXTO_SUAVE}>
                {buscando
                  ? 'Nenhuma empresa com esse nome, CNPJ ou ID.'
                  : 'Nenhuma empresa neste grupo. Busque acima para adicionar.'}
              </Text>
            )}
          </>
        )}
      </Cartao>

      {inativasDoGrupo.length > 0 && (
        <Cartao p="14px 20px" display="flex" flexDirection="column" gap="8px">
          <Flex align="center" gap="8px" wrap="wrap">
            <Etiqueta tom="neutro">
              {inativasDoGrupo.length} {inativasDoGrupo.length === 1 ? 'inativa' : 'inativas'}
            </Etiqueta>
            <Text fontSize="12.5px" color={TEXTO_SUAVE} flex="1 1 260px">
              Continuam neste grupo, mas não aparecem nas carteiras nem nas outras abas. Para mexer
              nelas, reative em “Situação das empresas”.
            </Text>
            <Button
              size="xs"
              variant="ghost"
              borderRadius="999px"
              onClick={() => setVerInativas(v => !v)}
            >
              {verInativas ? 'Esconder' : 'Ver quais'}
            </Button>
          </Flex>
          {verInativas &&
            inativasDoGrupo.map(e => {
              const m = situacaoDaEmpresa(e).motivo;
              return (
                <Flex key={e.id} align="center" gap="10px" fontSize="12.5px" opacity={0.8}>
                  <Buildings size={14} color={TEXTO_SUAVE} />
                  <Text flex="1 1 200px" color="#475569" noOfLines={1}>
                    {e.nome}
                  </Text>
                  <Text color={TEXTO_SUAVE} noOfLines={1} title={detalheDoMotivo(e)}>
                    {m ? MOTIVOS[m].rotulo : ''}
                  </Text>
                </Flex>
              );
            })}
        </Cartao>
      )}

      <Text fontSize="12px" color={TEXTO_SUAVE}>
        Atenção: a importação de planilha de empresas também grava o grupo de empresas (coluna
        &quot;Grupo de Empresas&quot;) e sobrescreve o que for definido aqui.
      </Text>

      <BarraSalvar
        texto={
          mudou
            ? `${entram} ${entram === 1 ? 'empresa entra' : 'empresas entram'} e ${saem} ${
                saem === 1 ? 'sai' : 'saem'
              } de ${nome}${
                vindasDeOutro.length ? ` (${vindasDeOutro.length} vindas de outro grupo)` : ''
              }. Só é gravado ao salvar.`
            : 'Nenhuma alteração pendente.'
        }
        semMudanca={!mudou}
        salvando={salvar.isPending}
        onDesfazer={() => setEmpresas(base)}
        onSalvar={() => salvar.mutate()}
      />
    </Flex>
  );
}
